import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';

export interface LeftMenuResponse {
  id: number;
  label: string;
  link?: string | null;

  parent?: number | null;
  order?: number | null;
  faIcon?: string | null;
  children?: LeftMenuResponse[];
}

export interface MenuItem {
  id: number;
  title: string;
  url?: string;
  faIcon?: string;
  order?: number;
  children?: MenuItem[];
}

type MenuApiResponse =
  | LeftMenuResponse
  | {
      data?: LeftMenuResponse | null;
      result?: LeftMenuResponse | null;
    };

@Injectable({
  providedIn: 'root',
})
export class RmsMenuService {
  private readonly http = inject(HttpClient);

  private readonly menuSubject = new BehaviorSubject<MenuItem[]>([]);

  readonly menu$ = this.menuSubject.asObservable();

  private readonly menuApiUrl = `${environment.API_URL}/menu/rms/menu`;

  loadMenu(server: string): Observable<MenuItem[]> {
    const params = new HttpParams().set('server', server);

    return this.http.get<MenuApiResponse>(this.menuApiUrl, { params }).pipe(
      map((response) => this.unwrapResponse(response)),
      map((root) => this.toMenuItems(root?.children ?? [])),
      tap((menus) => this.menuSubject.next(menus)),
    );
  }

  getMenus(): MenuItem[] {
    return this.menuSubject.value;
  }

  setMenus(menus: MenuItem[]): void {
    this.menuSubject.next(menus);
  }

  findMenuByUrl(url: string): MenuItem | undefined {
    const normalized = this.normalizeUrl(url);

    return this.flattenMenus(this.menuSubject.value)
      .filter((menu) => !!menu.url)
      .sort((a, b) => (b.url?.length ?? 0) - (a.url?.length ?? 0))
      .find((menu) => {
        const path = this.normalizeUrl(menu.url!);
        return normalized === path || normalized.startsWith(`${path}/`);
      });
  }

  private unwrapResponse(response: MenuApiResponse | null): LeftMenuResponse | null {
    if (!response) return null;
    if ('id' in response) return response;
    if ('data' in response && response.data) return response.data;
    if ('result' in response) return response.result ?? null;
    return null;
  }

  private toMenuItems(items: LeftMenuResponse[]): MenuItem[] {
    return [...items]
      .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999))
      .map((item) => ({
        id: item.id,
        title: item.label,
        url: item.link || undefined,
        faIcon: item.faIcon || undefined,
        order: item.order ?? undefined,
        children: item.children?.length ? this.toMenuItems(item.children) : undefined,
      }));
  }

  private flattenMenus(menus: MenuItem[]): MenuItem[] {
    return menus.flatMap((menu) => [menu, ...this.flattenMenus(menu.children ?? [])]);
  }

  private normalizeUrl(url: string): string {
    const path = url.split('?')[0].split('#')[0];
    return path.length > 1 ? path.replace(/\/+$/, '') : path;
  }
}
